import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { dataHora, plural } from '../formato';
import { useSessao } from '../sessao';
import { Aviso, Carregando, Dialogo, ErroDaApi } from '../ui';
import { LigarPrevistas } from './LigarPrevistas';
import type { AmostraPrevista, Os } from './OsForms';
import { daApi, tituloDaAmostra } from '../NomeDaAmostra';

/**
 * 281 · vincular, pela OS, um recebimento que chegou sem OS.
 *
 * «Tem como vincular um Recebimento já feito (que não foi vinculado
 *  inicialmente a uma OS) a uma OS?» … «Sim, e me mostre como fica» —
 * Humberto, 06/10/2026.
 *
 * Já dava, mas só pela ficha do lote (cartão «Este recebimento está sem
 * OS»): quem está NA OS, olhando o que falta chegar, não tinha como puxar
 * o lote que chegou solto. Aqui, o Resumo da OS avisa quando há lote sem
 * OS do MESMO cliente (o gatilho da `0017` recusa outro cliente) e abre a
 * janela em dois passos: 1) escolher o lote — dizendo quais nomes casam
 * com as previstas da OS; 2) ligar às previstas (o `LigarPrevistas` de
 * sempre, dentro da janela). «Agora não» deixa só o vínculo.
 *
 * A gravação é a mesma da ficha do lote (`PATCH /recebimento/:id/os`,
 * `atribuir_os_ao_lote`): a OS desce para o lote e para todas as amostras.
 */

interface LoteSemOs {
  id: string;
  codigo: string;
  datahora_recebimento: string;
  amostras: string | number;
  status: string;
  responsavel_entrega_nome: string | null;
}
interface AmostraDoLote {
  id: string;
  codigo: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
}

/** «SP-01/1», «sp 01 / 1» e «SP01/1» são o mesmo nome. */
const chato = (s: string | null | undefined) => (s ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[\s\-_.]/g, '');

/** O aviso no cartão «Recebimentos» do Resumo — e a janela que ele abre. */
export function AvisoSemOs({ os, previstas, aoVincular }: {
  os: Os;
  previstas: AmostraPrevista[];
  /** Recarrega a OS: o lote vinculado entra em «Recebimentos». */
  aoVincular?: () => void | Promise<void>;
}) {
  const { pode } = useSessao();
  const permitido = pode('recebimento.atribuir_os') && !os.cancelada && !os.suspensa
    && !!os.cliente_id;
  const [lotes, setLotes] = useState<LoteSemOs[] | null>(null);
  const [aberto, setAberto] = useState(false);

  const carregar = useCallback(async () => {
    if (!permitido) return;
    try {
      setLotes(await api<LoteSemOs[]>(
        `/recebimento?semOs=1&cliente=${encodeURIComponent(os.cliente_id)}`));
    } catch { setLotes([]); }
  }, [permitido, os.cliente_id]);
  useEffect(() => { void carregar(); }, [carregar]);

  if (!permitido || !lotes || lotes.length === 0) return null;

  return (
    <>
      <div className="ro-sem-os" role="status">
        <div>
          <b>▲ {plural(lotes.length, 'recebimento sem OS', 'recebimentos sem OS')} deste cliente</b>
          <span className="sub">
            {lotes.slice(0, 3).map((l) => l.codigo).join(', ')}
            {lotes.length > 3 ? ` e mais ${lotes.length - 3}` : ''}
          </span>
        </div>
        <button type="button" className="botao secundario" onClick={() => setAberto(true)}>
          Vincular
        </button>
      </div>
      {aberto && (
        <JanelaVincular os={os} lotes={lotes} previstas={previstas}
          aoVincular={async () => { await aoVincular?.(); }}
          aoFechar={() => { setAberto(false); void carregar(); }} />
      )}
    </>
  );
}

function JanelaVincular({ os, lotes, previstas, aoVincular, aoFechar }: {
  os: Os;
  lotes: LoteSemOs[];
  previstas: AmostraPrevista[];
  aoVincular(): Promise<void>;
  aoFechar(): void;
}) {
  const [escolhido, setEscolhido] = useState<string>(lotes.length === 1 ? lotes[0].id : '');
  const [amostrasDe, setAmostrasDe] = useState<Record<string, AmostraDoLote[]>>({});
  const [vinculado, setVinculado] = useState<LoteSemOs | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  /* Os nomes das amostras de cada lote: é o que diz «casa com as previstas». */
  useEffect(() => {
    let vivo = true;
    for (const l of lotes.slice(0, 10)) {
      api<{ amostras: AmostraDoLote[] }>(`/recebimento/${l.id}`)
        .then((r) => { if (vivo) setAmostrasDe((m) => ({ ...m, [l.id]: r.amostras })); })
        .catch(() => { /* sem os nomes, a escolha continua possível */ });
    }
    return () => { vivo = false; };
  }, [lotes]);

  const abertas = new Set(previstas.filter((p) => !p.atendida)
    .map((p) => chato(p.identificacao_prevista)).filter(Boolean));
  const casam = (loteId: string) => (amostrasDe[loteId] ?? [])
    .filter((a) => abertas.has(chato(a.identificacao_recebida))).length;

  async function vincular() {
    const lote = lotes.find((l) => l.id === escolhido);
    if (!lote) return;
    setErro(null); setOcupado(true);
    try {
      await api(`/recebimento/${lote.id}/os`, { metodo: 'PATCH', corpo: { osId: os.id } });
      setVinculado(lote);
      await aoVincular();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  if (vinculado) {
    return (
      <Dialogo aberto rotulo={`Recebimento ${vinculado.codigo} · OS ${os.numero}`}
        titulo="Ligar às previstas da OS" aoFechar={aoFechar}>
        <div className="vincular-os">
          <Aviso tipo="ok" titulo={`O recebimento ${vinculado.codigo} agora é da OS ${os.numero}.`}>
            A OS passou para o lote e para todas as amostras dele. Ligue cada
            amostra à prevista dela para trazer os ensaios marcados — ou deixe
            para depois.
          </Aviso>
          <LigarPrevistas osId={os.id} loteId={vinculado.id} aoLigar={aoVincular} />
        </div>
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Agora não</button>
          <button type="button" className="botao" onClick={aoFechar}>Concluir</button>
        </div>
      </Dialogo>
    );
  }

  return (
    <Dialogo aberto rotulo={`OS ${os.numero}`} titulo="Vincular recebimento sem OS"
      aoFechar={aoFechar}>
      <div className="vincular-os">
        <ErroDaApi erro={erro} />
        <p>
          Recebimentos de <b>{os.cliente}</b> que chegaram sem OS. Ao vincular, a
          OS <span className="mono">{os.numero}</span> passa para o lote e para
          todas as amostras dele.
        </p>
        <div className="vincular-lista" role="radiogroup" aria-label="Recebimento a vincular">
          {lotes.map((l) => {
            const nomes = amostrasDe[l.id];
            const n = casam(l.id);
            return (
              <label key={l.id} className={`vincular-opcao${escolhido === l.id ? ' sel' : ''}`}
                data-lote={l.codigo}>
                <input type="radio" name="lote-sem-os" value={l.id}
                  checked={escolhido === l.id} onChange={() => setEscolhido(l.id)} />
                <span className="vincular-texto">
                  <span>
                    <b className="mono">{l.codigo}</b>
                    {' · '}<span className="mono">{dataHora(l.datahora_recebimento)}</span>
                    {' · '}{plural(Number(l.amostras), 'amostra', 'amostras')}
                    {l.status !== 'concluido' && ' · em recebimento'}
                  </span>
                  {nomes === undefined ? <Carregando texto="Lendo as amostras" /> : (
                    <span className="sub">
                      {nomes.slice(0, 4)
                        // 303 (RN-248): pelo nome do cliente; sem ele, o código.
                        .map((a) => tituloDaAmostra(daApi(a))).join(', ')}
                      {nomes.length > 4 ? ` e mais ${nomes.length - 4}` : ''}
                    </span>
                  )}
                  {nomes !== undefined && (n > 0
                    ? <span className="ok">✓ {plural(n, 'nome casa', 'nomes casam')} com as previstas desta OS</span>
                    : <span className="sub">nenhum nome casa com as previstas desta OS</span>)}
                  {l.responsavel_entrega_nome && (
                    <span className="sub">entregou {l.responsavel_entrega_nome}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={ocupado}>
          Cancelar
        </button>
        <button type="button" className="botao" disabled={!escolhido || ocupado} onClick={() => { void vincular(); }}>
          {ocupado ? 'Vinculando…' : `Vincular à OS ${os.numero}`}
        </button>
      </div>
    </Dialogo>
  );
}
